/**
 * Design system: add to cart from the product card and the bundle picker.
 *
 * "Add to cart" adds in the background and opens the cart drawer (Savor's own
 * behaviour, theme setting "Automatically open cart drawer"), so the shopper sees the
 * checkout button straight away. Once the product is in the cart, its button becomes a
 * − count + stepper; + and − change the quantity quietly, without reopening the drawer.
 * At zero the "Add to cart" button comes back. Every change fires Savor's cart:update
 * event, so the header count and the cart drawer stay in sync, and changes made in the
 * drawer flow back into the steppers. Without JavaScript the forms post to the cart.
 */
import { CartAddEvent, ThemeEvents } from '@theme/events';

const FORMS = '.ms-product-card__form, .home-feature__form';
const SOURCE = 'ms-quick-add';

/** @type {Map<number, {key: string, quantity: number}>} variant id -> cart line */
let lines = new Map();
let busy = false;

/** @param {any} cart */
function readCart(cart) {
  lines = new Map();
  for (const item of cart.items || []) {
    const current = lines.get(item.variant_id);
    lines.set(item.variant_id, { key: item.key, quantity: (current?.quantity || 0) + item.quantity });
  }
}

function sectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);
}

/** @param {HTMLFormElement} form */
function variantOf(form) {
  const input = form.querySelector('input[name="id"]:checked') || form.querySelector('input[name="id"]');
  return input instanceof HTMLInputElement ? Number(input.value) : 0;
}

/** @param {HTMLFormElement} form */
function stepperFor(form) {
  let stepper = form.querySelector('.ms-stepper');
  if (stepper) return stepper;
  const name = form.closest('.ms-product-card')?.querySelector('.ms-product-card__title')?.textContent?.trim() || 'this product';
  stepper = document.createElement('div');
  stepper.className = 'ms-stepper';
  stepper.hidden = true;
  stepper.innerHTML = `
    <button type="button" class="ms-stepper__button" data-step="-1" aria-label="Remove one ${name}">−</button>
    <output class="ms-stepper__count" aria-live="polite"></output>
    <button type="button" class="ms-stepper__button" data-step="1" aria-label="Add one ${name}">+</button>`;
  form.querySelector('[type="submit"]')?.after(stepper);
  return stepper;
}

/** Show the stepper or the add button on every form, from the cart we hold. */
function render() {
  for (const form of document.querySelectorAll(FORMS)) {
    if (!(form instanceof HTMLFormElement)) continue;
    const submit = form.querySelector('[type="submit"]');
    if (!(submit instanceof HTMLElement)) continue;
    const stepper = stepperFor(form);
    const quantity = lines.get(variantOf(form))?.quantity || 0;
    const count = stepper.querySelector('.ms-stepper__count');
    if (count) count.textContent = String(quantity);
    stepper.hidden = quantity === 0;
    submit.hidden = quantity > 0;
  }
}

/**
 * Tell the theme the cart changed. Savor's drawer opens on every cart:update while it
 * has `auto-open`; for quiet stepper changes the attribute is lifted for the duration
 * of the (synchronous) dispatch.
 * @param {Element} source @param {any} cart @param {any} sections @param {boolean} openDrawer
 */
function announce(source, cart, sections, openDrawer) {
  const drawer = document.querySelector('cart-drawer-component');
  const autoOpen = drawer?.hasAttribute('auto-open');
  if (!openDrawer && autoOpen) drawer?.removeAttribute('auto-open');
  source.dispatchEvent(new CartAddEvent(cart, SOURCE, { itemCount: cart.item_count, sections }));
  if (!openDrawer && autoOpen) drawer?.setAttribute('auto-open', '');
}

/**
 * @param {string} url @param {Record<string, string | number>} body
 * @param {Element} source @param {boolean} openDrawer
 */
async function change(url, body, source, openDrawer) {
  const ids = sectionIds();
  const payload = { ...body, ...(ids.length ? { sections: ids.join(','), sections_url: window.location.pathname } : {}) };
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok || data.status) throw new Error(data.description || data.message || 'Cart not updated');

  // /cart/add.js answers with the line, /cart/change.js with the whole cart
  const cart = data.items && 'item_count' in data ? data : await (await fetch('/cart.js')).json();
  readCart(cart);
  render();
  announce(source, cart, data.sections, openDrawer);
}

/** @param {Element | null} element @param {boolean} on */
function setBusy(element, on) {
  busy = on;
  element?.closest('form')?.querySelectorAll('button').forEach((button) => {
    if (on) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  });
}

// "Add to cart": add one, open the drawer, the stepper takes over on the card
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches(FORMS)) return;
  event.preventDefault();
  if (busy) return;
  const submit = form.querySelector('[type="submit"]');
  setBusy(form, true);
  try {
    await change('/cart/add.js', { id: variantOf(form), quantity: 1 }, form, true);
  } catch (error) {
    if (submit instanceof HTMLElement) {
      const label = submit.textContent;
      submit.textContent = 'Try again';
      setTimeout(() => (submit.textContent = label), 2000);
    }
  } finally {
    setBusy(form, false);
  }
});

// − and +: quiet changes, the drawer stays closed
document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('.ms-stepper__button') : null;
  if (!(button instanceof HTMLElement) || busy) return;
  const form = button.closest('form');
  if (!(form instanceof HTMLFormElement)) return;
  const variant = variantOf(form);
  const line = lines.get(variant);
  setBusy(button, true);
  try {
    if (Number(button.dataset.step) > 0) await change('/cart/add.js', { id: variant, quantity: 1 }, form, false);
    else if (line) await change('/cart/change.js', { id: line.key, quantity: Math.max(0, line.quantity - 1) }, form, false);
  } catch (error) {
    // keep the current numbers; the next cart read corrects them
  } finally {
    setBusy(button, false);
  }
});

// Bundle picker: the stepper follows the selected bundle
document.addEventListener('change', (event) => {
  if (event.target instanceof HTMLInputElement && event.target.name === 'id' && event.target.closest(FORMS)) render();
});

// Changes made elsewhere (cart drawer, product page) flow back in
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (/** @type {CustomEvent} */ (event).detail?.sourceId === SOURCE) return;
  fetch('/cart.js')
    .then((response) => response.json())
    .then((cart) => {
      readCart(cart);
      render();
    })
    .catch(() => {});
});

// Start from what is already in the cart
fetch('/cart.js')
  .then((response) => response.json())
  .then((cart) => {
    readCart(cart);
    render();
  })
  .catch(() => {});
